import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Wallet, CreditCard, Landmark, AlertTriangle, CheckCircle, MapPin, Plus } from "lucide-react";
import { api, apiError, PLACEHOLDER_IMAGE } from "@/api";

const money = (v = 0) => `₹${Number(v).toLocaleString("en-IN")}`;

const PAY_METHODS = [
  { id: "wallet", icon: Wallet, label: "DMMobile Wallet", sub: "Instant debit from your balance" },
  { id: "bank_transfer", icon: Landmark, label: "UPI / Bank Transfer", sub: "Pay manually & upload proof" },
  { id: "upi", icon: CreditCard, label: "Razorpay (UPI/Card/Net banking)", sub: "Secure online payment" },
];

export default function AuctionWinnerPayment({ user, common }) {
  const navigate = useNavigate();
  const [wonAuctions, setWonAuctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAuction, setSelectedAuction] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState("");
  const [payMethod, setPayMethod] = useState("wallet");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) { navigate("/login?next=/auctions"); return; }
    api.get("/api/auctions/won")
      .then(({ data }) => setWonAuctions(data))
      .catch(() => toast.error("Could not load won auctions"))
      .finally(() => setLoading(false));
  }, [user, navigate]);

  const addresses = user?.addresses || [];

  // Auto-select first address
  useEffect(() => {
    if (addresses.length && !selectedAddress) {
      setSelectedAddress(addresses[0].id);
    }
  }, [addresses, selectedAddress]);

  const handlePay = async () => {
    if (!selectedAuction) return;
    if (!selectedAddress) { toast.error("Please select a delivery address"); return; }
    setBusy(true);
    try {
      const { data: order } = await api.post(
        `/api/auctions/${selectedAuction.id}/winner-order`,
        { address_id: selectedAddress, payment_method: payMethod }
      );
      toast.success("Order created successfully!");
      if (payMethod === "bank_transfer") {
        navigate(`/my-orders/${order.id}?manual=1`);
      } else if (payMethod === "wallet") {
        navigate(`/my-orders/${order.id}`);
      } else {
        navigate(`/my-orders/${order.id}`);
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const Topbar = common?.Topbar;
  const FooterBar = common?.FooterBar;
  const BottomNav = common?.BottomNav;
  const Loading = common?.Loading;

  if (loading) {
    return (
      <>
        {Topbar && <Topbar {...(common || {})} />}
        <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {Loading ? <Loading /> : <p style={{ color: "#6b7f9e" }}>Loading…</p>}
        </div>
      </>
    );
  }

  if (!user) return null;

  return (
    <>
      {Topbar && <Topbar {...(common || {})} />}
      <main style={{ background: "#060d1a", minHeight: "100vh", paddingTop: 20 }}>
        <div className="phi-winner-page">
          <Link to="/auctions" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#6b7f9e", fontSize: 12, textDecoration: "none", marginBottom: 24 }} data-testid="winner-back-auctions">
            ← Back to Auctions
          </Link>

          {wonAuctions.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px" }}>
              <Wallet size={48} style={{ color: "#1a2845", margin: "0 auto 16px", display: "block" }} />
              <h2 style={{ color: "#eef2fb", marginBottom: 8 }}>No Won Auctions</h2>
              <p style={{ color: "#6b7f9e", marginBottom: 24 }}>You haven't won any auctions yet. Join a live auction to win!</p>
              <Link to="/auctions" className="phi-btn-primary" data-testid="winner-go-auctions">Browse Auctions</Link>
            </div>
          ) : !selectedAuction ? (
            <>
              <div className="phi-winner-banner" data-testid="winner-banner">
                <span className="phi-winner-crown" role="img" aria-label="trophy">🏆</span>
                <h2>Congratulations! You Won!</h2>
                <p>Select the auction below to complete your payment and get your device shipped.</p>
              </div>

              <h3 style={{ color: "#8b9abb", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 14 }}>Your Won Auctions</h3>

              {wonAuctions.map((auction) => (
                <div
                  key={auction.id}
                  className="phi-winner-product"
                  style={{ cursor: auction.existing_order ? "default" : "pointer" }}
                  onClick={() => { if (!auction.existing_order) setSelectedAuction(auction); }}
                  data-testid={`won-auction-${auction.id}`}
                >
                  <img
                    src={auction.product?.image || PLACEHOLDER_IMAGE}
                    alt={auction.product?.name}
                    className="phi-winner-product-img"
                    onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                  />
                  <div style={{ flex: 1 }}>
                    <h3>{auction.product?.name || "Auction Device"}</h3>
                    <span className="phi-winning-bid">
                      {money(auction.current_bid)} <small>Winning bid</small>
                    </span>
                    {auction.existing_order ? (
                      <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#34d399" }}>
                        <CheckCircle size={14} />
                        Order placed — #{auction.existing_order.order_number}
                        <Link to={`/my-orders/${auction.existing_order.id}`} style={{ color: "var(--neon-blue)", fontWeight: 700, marginLeft: 4, textDecoration: "none" }} data-testid={`view-order-${auction.existing_order.id}`}>View</Link>
                      </div>
                    ) : (
                      <div style={{ marginTop: 8, fontSize: 11, color: "var(--neon-blue)", fontWeight: 700 }}>
                        Tap to Pay & Claim →
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              {/* Back to list */}
              <button
                onClick={() => setSelectedAuction(null)}
                style={{ background: "none", border: "none", color: "#6b7f9e", cursor: "pointer", fontSize: 12, padding: 0, marginBottom: 20, display: "flex", alignItems: "center", gap: 6 }}
                data-testid="winner-back-list"
              >
                ← All Won Auctions
              </button>

              <div className="phi-winner-banner" data-testid="winner-payment-banner">
                <span className="phi-winner-crown" role="img" aria-label="trophy">🏆</span>
                <h2>Complete Your Payment</h2>
                <p>Pay for your winning bid to claim your device</p>
              </div>

              {/* Product summary */}
              <div className="phi-winner-product" data-testid="winner-product-summary">
                <img
                  src={selectedAuction.product?.image || PLACEHOLDER_IMAGE}
                  alt={selectedAuction.product?.name}
                  className="phi-winner-product-img"
                  onError={(e) => { e.currentTarget.src = PLACEHOLDER_IMAGE; }}
                />
                <div>
                  <h3>{selectedAuction.product?.name}</h3>
                  <span className="phi-winning-bid">
                    {money(selectedAuction.current_bid)} <small>Winning bid</small>
                  </span>
                </div>
              </div>

              {/* Address selection */}
              <div className="phi-address-select" data-testid="winner-address-section">
                <h4 style={{ fontSize: 12, color: "#8b9abb", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                  <MapPin size={14} /> Delivery Address
                </h4>
                {addresses.length === 0 ? (
                  <div style={{ padding: 16, textAlign: "center", background: "rgba(10,19,38,0.5)", borderRadius: 14, border: "1px dashed rgba(255,255,255,0.1)" }}>
                    <p style={{ color: "#6b7f9e", fontSize: 13, margin: "0 0 10px" }}>No addresses saved</p>
                    <Link to="/account" className="phi-btn-outline" style={{ fontSize: 12 }} data-testid="winner-add-address">
                      <Plus size={12} /> Add Address
                    </Link>
                  </div>
                ) : (
                  addresses.map((addr) => (
                    <div
                      key={addr.id}
                      className={`phi-address-card${selectedAddress === addr.id ? " on" : ""}`}
                      onClick={() => setSelectedAddress(addr.id)}
                      data-testid={`winner-addr-${addr.id}`}
                    >
                      <b>{addr.name} — {addr.phone}</b>
                      <p>{addr.line1}, {addr.city}, {addr.state} {addr.pincode}</p>
                    </div>
                  ))
                )}
              </div>

              {/* Payment method */}
              <div data-testid="winner-payment-methods">
                <h4 style={{ fontSize: 12, color: "#8b9abb", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 12 }}>
                  Payment Method
                </h4>
                <div className="phi-payment-methods">
                  {PAY_METHODS.map(({ id, icon: Icon, label, sub }) => (
                    <div
                      key={id}
                      className={`phi-pay-method${payMethod === id ? " on" : ""}`}
                      onClick={() => setPayMethod(id)}
                      data-testid={`winner-pay-${id}`}
                    >
                      <div className="phi-pay-method-icon"><Icon size={18} /></div>
                      <div><b>{label}</b><small>{sub}</small></div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Wallet balance warning */}
              {payMethod === "wallet" && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 16px", background: "rgba(251,191,36,0.07)", border: "1px solid rgba(251,191,36,0.2)", borderRadius: 12, marginBottom: 20, fontSize: 12, color: "#fbbf24" }}>
                  <AlertTriangle size={14} />
                  Wallet balance: {money(user.balance || 0)} — Make sure you have sufficient balance.
                </div>
              )}

              {/* Place Order */}
              <button
                className="phi-sell-next-btn"
                style={{ width: "100%" }}
                onClick={handlePay}
                disabled={busy || !selectedAddress}
                data-testid="winner-place-order-btn"
              >
                {busy ? "Processing…" : `Pay ${money(selectedAuction.current_bid)} & Claim Device →`}
              </button>

              <p style={{ fontSize: 11, color: "#6b7f9e", textAlign: "center", marginTop: 12 }}>
                By paying, you agree to complete the purchase. Auction wins are non-refundable except for verified device condition issues.
              </p>
            </>
          )}
        </div>
      </main>
      {FooterBar && <FooterBar />}
      {BottomNav && <BottomNav active="Auction" />}
    </>
  );
}
